// One tappable row: a leading mark, a title (and subtitle), and one trailing affordance.
// `ActionRowView` is the plain drawing, reused by the session mail cards; Flutter twin: actionrow.dart.
import { Icon } from './primitives';
import { resetButton } from './ui';

const TONES = {
  neutral: { ink: 'var(--text-2)', wash: 'var(--surface-3)' },
  accent: { ink: 'var(--accent)', wash: 'var(--accent-soft)' },
  warn: { ink: 'var(--warn)', wash: 'var(--warn-soft)' },
  danger: { ink: 'var(--bad)', wash: 'var(--bad-soft)' },
  success: { ink: 'var(--good)', wash: 'var(--good-soft)' },
};

/** `tone` → `{ink, wash}`; a hex brand colour washes at 10%. */
function actionRowTone(tone) {
  if (TONES[tone]) return TONES[tone];
  if (typeof tone === 'string' && /^#[0-9a-f]{6}$/i.test(tone)) return { ink: tone, wash: `${tone}1A` };
  return TONES.neutral;
}

const VARIANTS = {
  card: { background: 'var(--surface)', borderRadius: 16, boxShadow: 'var(--shadow-1)', padding: '10px 12px', gap: 12 },
  outline: { background: 'var(--surface)', borderRadius: 12, border: '1px solid var(--line)', padding: '12px 14px', gap: 10 },
  inset: { borderTop: '1px solid var(--line)', padding: '8px 12px', gap: 10 },
};

function Mark({ icon, glyph, tone, iconStyle, small }) {
  const { ink, wash } = actionRowTone(tone);
  if (!icon && !glyph) return null;
  const drawn = glyph || <Icon props={{ name: icon, size: small ? 14 : 16, tone: ink }} />;
  if (iconStyle === 'bare') {
    return <span data-action-row-mark="bare" style={{ display: 'inline-flex', flex: '0 0 auto', color: ink }}>{drawn}</span>;
  }
  const round = iconStyle === 'badge';
  const size = round ? 28 : 32;
  return (
    <span data-action-row-mark={round ? 'badge' : 'tile'} style={{
      flex: `0 0 ${size}px`, width: size, height: size, borderRadius: round ? '50%' : 8, background: wash,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    }}>{drawn}</span>
  );
}

export function ActionRowView({
  variant = 'card', icon, tone, iconStyle, title, subtitle, subtitleTone, titleLines = 2,
  trailing, actionLabel, actionBusy = false, onActivate, onAction, children, glyph, after,
}) {
  const box = VARIANTS[variant] || VARIANTS.card;
  const inset = variant === 'inset';
  const wash = inset ? actionRowTone(tone).wash : undefined;
  const strong = Boolean(subtitle) || iconStyle === 'badge';
  const lines = Math.min(2, Math.max(1, Number(titleLines) || 2));
  const fireAction = (e) => { e.stopPropagation(); if (!actionBusy) onAction?.(); };
  return (
    <div
      data-action-row={variant}
      role={onActivate ? 'button' : undefined}
      tabIndex={onActivate ? 0 : undefined}
      onClick={onActivate}
      style={{
        display: 'flex', alignItems: 'center', minWidth: 0, boxSizing: 'border-box', width: '100%',
        cursor: onActivate ? 'pointer' : 'default', ...box, ...(wash ? { background: wash } : null),
      }}
    >
      <Mark icon={icon} glyph={glyph} tone={tone} iconStyle={iconStyle || (inset || variant === 'outline' ? 'bare' : 'tile')} small={inset} />
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span data-action-row-title="" style={{
          fontSize: inset ? 12 : 14, fontWeight: inset || variant === 'outline' ? 400 : (strong ? 600 : 500),
          color: 'var(--text)', lineHeight: 1.35,
          display: '-webkit-box', WebkitLineClamp: lines, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}>{title}</span>
        {subtitle ? (
          <span data-action-row-subtitle="" style={{
            fontSize: 12, fontWeight: subtitleTone ? 500 : 400,
            color: subtitleTone ? actionRowTone(subtitleTone).ink : 'var(--text-3)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{subtitle}</span>
        ) : null}
        {children}
      </div>
      {actionLabel ? (
        <button type="button" data-action-row-action="" aria-busy={actionBusy || undefined} onClick={fireAction} style={{
          ...resetButton, flex: '0 0 auto', height: inset ? 28 : 32, padding: inset ? '0 12px' : '0 14px',
          borderRadius: 999, background: inset ? 'var(--text-2)' : 'var(--text)', color: 'var(--surface)',
          fontSize: inset ? 12 : 13, fontWeight: 600, whiteSpace: 'nowrap', cursor: actionBusy ? 'default' : 'pointer',
          opacity: actionBusy ? 0.7 : 1,
        }}>{actionLabel}</button>
      ) : null}
      {!actionLabel && (trailing === 'chevron' || trailing === 'arrow') ? (
        <span aria-hidden style={{ flex: '0 0 auto', display: 'inline-flex', color: 'var(--text-3)' }}>
          <Icon props={{ name: trailing === 'arrow' ? 'arrow-up-right' : 'chevron', size: 14, tone: 'var(--text-3)' }} />
        </span>
      ) : null}
      {after || null}
    </div>
  );
}

export function ActionRow({ variant, props = {}, emit }) {
  const title = props.title == null ? '' : String(props.title);
  if (!title) return null;
  return (
    <ActionRowView
      variant={variant || props.variant || 'card'}
      icon={props.icon}
      tone={props.tone}
      iconStyle={props.iconStyle}
      title={title}
      subtitle={props.subtitle == null ? '' : String(props.subtitle)}
      subtitleTone={props.subtitleTone}
      titleLines={props.titleLines}
      trailing={props.trailing}
      actionLabel={props.actionLabel ? String(props.actionLabel) : ''}
      onActivate={emit ? () => emit('activated', {}) : undefined}
      onAction={emit ? () => emit('actionRequested', {}) : undefined}
    />
  );
}
